import React from "react";
import type { Placement } from "@floating-ui/react";
import { useTranslation } from "react-i18next";
import {
  generateClassNameWrapper,
  type ButtonStyle,
} from "../ButtonUtils/ButtonUtils";
import "./TextButton.scss";
import { getDataAnalyticsProps } from "../../../../utils";
import { Tooltip } from "../../../components";
import { useIcon } from "../../../assets/useIcon";

type TextButtonSize = "small" | "medium" | "large" | "xl";

export interface TextButtonActionState {
  state?: "idle" | "error" | "submitting" | "success";
  tooltip?: string;
}

export interface TextButtonProps {
  label?: string;
  size?: TextButtonSize | string;
  leftImg?: string;
  rightIcon?: string;
  leftIcon?: string;
  rightSvgIcon?: string;
  leftSvgIcon?: string;
  isFullWidth?: boolean;
  isSquare?: boolean;
  actionState?: TextButtonActionState;
  aiStyle?: boolean;
  rightLabel?: string;
  // Shared button props (mirrors ButtonUtils `sharedProps`).
  /**
   * Visual variant. `primary` = purple filled emphasis variant;
   * `secondary` = outlined low-emphasis variant (currently the same visual as `default`).
   */
  buttonStyle?: ButtonStyle | "" | false;
  isSubmitting?: boolean;
  className?: string;
  wrapperClassName?: string;
  withTooltip?: {
    place?: Placement;
    // Passed through to Tooltip, which renders any ReactNode.
    content?: React.ReactNode;
    className?: string;
  };
  title?: string;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  name?: string;
  "data-testid"?: string;
  [key: string]: any;
}

const actionStateIcons: Record<string, string> = {
  idle: "",
  error: "seem-icon-error",
  submitting: "seem-icon-spinner spinner",
  success: "seem-icon-checkmark",
};

const TextButton: React.FC<TextButtonProps> = ({
  label = "",
  size = "medium",
  leftImg = "",
  rightIcon = "",
  leftIcon = "",
  rightSvgIcon = "",
  leftSvgIcon = "",
  isFullWidth = false,
  isSquare = false,
  actionState,
  aiStyle = false,
  rightLabel,
  ...shared
}) => {
  const { getSvg } = useIcon();
  const { t } = useTranslation();

  const effectiveActionState: TextButtonActionState | undefined =
    shared?.buttonStyle === "menu" && !actionState
      ? { state: "idle" }
      : actionState;

  const specificProps = {
    "has-label": !!label,
    [`is-${size}`]: size,
    "has-right-label": !!rightLabel,
    "has-left-icon": !!leftIcon,
    "has-left-img": !!leftImg,
    "has-right-icon": !!rightIcon || !!effectiveActionState,
    "has-right-svg-icon": !!rightSvgIcon,
    "has-left-svg-icon": !!leftSvgIcon,
    "is-fullwidth": isFullWidth,
    "is-square": isSquare,
    "is-ai-style": aiStyle,
    "has-action-state": effectiveActionState,
    [`has-action-state-${effectiveActionState?.state}`]:
      effectiveActionState?.state,
  };

  const classNameWrapper = generateClassNameWrapper(shared, specificProps);

  return (
    <>
      <Tooltip
        type={"info"}
        placement={shared?.withTooltip?.place}
        content={shared?.withTooltip?.content}
        className={shared?.withTooltip?.className}
      >
        <div
          className={`form-button-wrap ${
            shared?.buttonStyle === "textLink"
              ? "form-textLink-button-wrap"
              : "form-text-button-wrap"
          } ${classNameWrapper} ${shared?.wrapperClassName || ""}`}
        >
          <button
            data-testid={`${shared["data-testid"]}-btn`}
            className={`form-button ${shared?.className || ""}`}
            type={shared?.type}
            title={shared?.title}
            disabled={shared?.disabled}
            onClick={(e) => shared?.onClick?.(e)}
            name={shared?.name}
            {...getDataAnalyticsProps(shared)}
          >
            {leftIcon && (
              <i className={`btn-left-icon seem-icon ${leftIcon}`} />
            )}
            {leftImg && <img className="btn-left-img" src={leftImg} alt="" />}

            {leftSvgIcon && (
              <img
                className="btn-left-svg-icon"
                src={getSvg(leftSvgIcon)}
                alt={leftSvgIcon}
              />
            )}

            {label && <div className="form-button-label">{t(label)}</div>}
            {rightLabel && <div className="btn-right-label">{rightLabel}</div>}
            {!!effectiveActionState?.state && (
              <Tooltip type="info" content={effectiveActionState.tooltip || ""}>
                <i
                  className={`btn-action-state-icon btn-right-icon seem-icon ${actionStateIcons[effectiveActionState.state]}`}
                />
              </Tooltip>
            )}

            {(shared?.buttonStyle === "menu" || rightIcon) && (
              <i
                className={`btn-right-icon seem-icon ${
                  shared?.buttonStyle === "menu"
                    ? "seem-icon-chevron-down"
                    : rightIcon
                }`}
              />
            )}
            {rightSvgIcon && (
              <img src={getSvg(rightSvgIcon)} alt={rightSvgIcon} />
            )}
          </button>
        </div>
      </Tooltip>
    </>
  );
};

export default TextButton;
